import { useCallback, useState } from "react";
import { Button, Col, Row, DropdownButton, Dropdown } from "react-bootstrap";
import ReactToPrint from "react-to-print";
import GlobalFilter from "./GlobalFilter";
import { ZButton } from "components/ZButton";
import { ZTooltip } from "components/ZTooltip";
import { ZIconType } from "helpers/utility/iconMap";
import { ZIcon } from "components/ZIcon";


export function Toolbar({ dataTable, isSearchable, disableExport, data, selectedRowsCount }: any) {
    const [quickAddValue, setQuickAddValue] = useState<string>("");

    function onSave() {
        let selectedProducts: any[] = [];
        data.selectedRows.forEach((row: any) => {
            row.forEach((product: any) => {
                selectedProducts.push(product);
            });
        });
        dataTable.saveSelectedRows(selectedProducts);
    }

    function handleAddQuickName(value: string) {
        dataTable.onQuickAdd(value);
        setQuickAddValue("");
    }

    const handleQuickAddKeyPress = (event: any) => {
        if (event.key === "Enter") {
            handleAddQuickName(quickAddValue);
            event.preventDefault(); 
        }
    };

    const reactToPrintContent = useCallback(() => { return dataTable.componentRef.current; }, [dataTable.componentRef]);
    const reactToPrintTrigger = useCallback(() => { return <Dropdown.Item className="d-inline-flex align-items-center gap-1"><ZIcon icon={ZIconType.Print} /> Print</Dropdown.Item>; }, []);

    const TopbarChild = () => {      
        return dataTable.topbarChild ? dataTable.topbarChild : null;
    };

    return (
        <Row className="mb-1 mt-1">
            <Col xl={5} lg={5} md={12}>
                {isSearchable &&
                    <div className="d-flex main-search pb-xg-0 pb-2">
                        <GlobalFilter
                            preGlobalFilteredRows={dataTable.preGlobalFilteredRows}
                            globalFilter={dataTable.state.globalFilter}
                            setGlobalFilter={dataTable.setGlobalFilter}
                            totalRows={dataTable.totalRows}
                            searchOnChange={dataTable.onSearchClick}
                            keywordChange={dataTable.keywordChange}
                            searchPlaceHolder={dataTable.searchPlaceHolder}
                            customSearchButton={dataTable.customSearchButton}
                        />
                        {dataTable.filterComponent && dataTable.filterComponent}
                    </div>
                }
            </Col>
            <Col xl={7} lg={7} md={12}>
                <div className="d-flex gap-1 justify-content-end flex-wrap text-xl-end text-sm-end mt-xl-0 ">
                    <div className="d-flex justify-content-start mt-1 mt-xl-0">
                        {/* Quick Add */}
                        {dataTable.allowQuickAdd &&
                        <div className="d-flex align-items-left">
                            <input
                                maxLength={100}
                                value={quickAddValue || ""}
                                onChange={(e) => setQuickAddValue(e.target.value)}
                                onKeyDown={handleQuickAddKeyPress}
                                placeholder={`Add new...`}
                                className="form-control w-auto"
                                style={{ minHeight: "38px", fontSize: 12, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                            />
                            <Button variant="primary" className="btn-icon custom-btn-icon filter-btn" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }} onClick={() => handleAddQuickName(quickAddValue)}>Add</Button>
                        </div>}
                    </div>

                    {/* Selected Rows */}
                    {dataTable.topbarChild === null && selectedRowsCount > 0 && <>
                        {<Button disabled variant="light" className="btn-icon custom-btn-icon mb-1">{selectedRowsCount}  Selected</Button>}
                    </>}
                    {/* Save Selected */}
                    {dataTable.saveSelectedRows !== null && <>
                        <Button variant="primary" className="btn-icon custom-btn-icon mb-1" onClick={onSave}>Add Selected</Button>
                    </>}

                    {/* Quick Add */}
                    {dataTable.allowQuickAdd &&
                    <div className="d-inline-block me-0">
                        <Col md={6} className="m-0 ps-1 pe-1" style={{ minWidth: "150px" }}>
                            <input
                                value={quickAddValue || ""}
                                onChange={(e) => setQuickAddValue(e.target.value)}
                                onKeyDown={handleQuickAddKeyPress}
                                placeholder={`Add new...`}
                                className="form-control w-auto ms-1 mb-1"
                            />
                        </Col>
                        <Col md={3} className="m-0 ps-1 pe-1 col-select">
                            <Button variant="primary" className="btn-icon custom-btn-icon filter-btn mb-1" onClick={() => handleAddQuickName(quickAddValue)}>Add</Button>
                        </Col>
                    </div>}

                    {/* Topbar Child */}
                    {dataTable.topbarChild !== null && <div className="d-inline-block me-0 mb-1">
                        <TopbarChild />
                    </div>}

                    {/* Add */}
                    {dataTable.addNewRow !== false &&
                        <ZTooltip text="Add">
                            <ZButton 
                                size="sm"
                                className="btn-icon custom-btn-icon"
                                onClick={dataTable.addNewRow} 
                                icon={ZIconType.Add}
                                iconSize="18px"
                            />
                        </ZTooltip>
                    }
                    
                    {dataTable.customAddButton != null && dataTable.customAddButton }
                    {/* Edit */}
                    {dataTable.isSelectable &&
                        (dataTable.handleSingleEdit || dataTable.handleBulkEdit) &&
                            <ZTooltip 
                                text={
                                    selectedRowsCount === 1 ? (
                                        dataTable.handleSingleEdit ? "Edit" : "Please select at least 2 rows to edit"
                                    ) : (selectedRowsCount > 1 ? (
                                        dataTable.handleBulkEdit ? `Edit ${selectedRowsCount} rows` : "Please select a single row to edit"
                                    ) : (dataTable.selectAllFilter ? (
                                        dataTable.handleBulkEdit ? `Edit all rows` : "Please select a single row to edit"
                                    ) : (dataTable.handleSingleEdit ? "Please select a row to edit" : "Please select at least 2 rows to edit")))
                                }
                                enabled={true}
                            >
                                <ZButton 
                                    size="sm"
                                    className="btn-icon custom-btn-icon"
                                    disabled={!((dataTable.selectAllFilter && dataTable.handleBulkEdit) || ((dataTable.handleSingleEdit && selectedRowsCount === 1) || (dataTable.handleBulkEdit && selectedRowsCount > 1)))}
                                    onClick={dataTable.selectAllFilter ? (dataTable.handleBulkEdit ?? dataTable.handleSingleEdit) : (selectedRowsCount > 1) ? dataTable.handleBulkEdit : dataTable.handleSingleEdit} 
                                    icon={ZIconType.Edit}
                                    iconSize="18px"
                                />
                            </ZTooltip>
                    }

                    {/* Merge */}
                    {dataTable.isSelectable && dataTable.merge !== false && dataTable.removeRows !== null &&
                        <ZTooltip text={
                            (selectedRowsCount > 1 || dataTable.selectAllFilter) ? 
                                (selectedRowsCount > 10 ? 
                                    "Cannot merge more than 10 lines at a time" : (!dataTable.merge.canMerge()) ? "Cannot merge multiple system lines" : "Merge"
                                ) 
                            : "Please select 2 or more items to merge"
                        }>
                            <ZButton
                                size="sm"
                                className="btn-icon custom-btn-icon"
                                disabled={!((selectedRowsCount > 1 && selectedRowsCount <= 10) || dataTable.selectAllFilter) || !dataTable.merge.canMerge()}
                                onClick={dataTable.merge.handleMerge} 
                                icon={ZIconType.Merge}
                                iconSize="18px"
                            />
                        </ZTooltip>
                    }

                    {/* Delete */}
                    {dataTable.isSelectable && <>
                        {dataTable.removeRows !== null &&
                            <ZTooltip text={selectedRowsCount > 0 || dataTable.selectAllFilter ? `Delete ${selectedRowsCount} rows` : "Please select a row to delete"}>
                                <ZButton 
                                    variant="light" 
                                    disabled={selectedRowsCount > 0 || dataTable.selectAllFilter ? false : true} 
                                    onClick={dataTable.handleDelete} 
                                    className="btn-icon custom-btn-icon"
                                    icon={ZIconType.Delete}
                                    iconSize="18px"
                                />
                            </ZTooltip>
                        }
                    </>}

                    {/* Reload */}
                    {dataTable.reloadTableRows !== false &&
                        <ZTooltip text="Refresh">
                            <ZButton 
                                variant="light"
                                className="btn-icon custom-btn-icon" 
                                onClick={() => dataTable.reloadTableRows()}
                                icon={ZIconType.Refresh}
                                iconSize="18px"
                            />
                        </ZTooltip>
                    }

                    {/* Cloud Sync */}
                    {dataTable.syncOptions !== false &&
                        <ZTooltip text="Sync">
                            <ZButton 
                                variant="light" 
                                size="sm" 
                                className="btn-icon custom-btn-icon" 
                                onClick={dataTable.syncOptions} 
                                icon={ZIconType.Sync} 
                                iconSize="18px"
                            />
                        </ZTooltip>
                    }
                    
                    {/* Import */}
                    {dataTable.singleImport !== false &&
                        <ZTooltip text="Import">
                            <ZButton
                                size="sm"
                                className="btn-icon custom-btn-icon"
                                onClick={dataTable.singleImport} 
                                icon={ZIconType.Upload}
                                iconSize="18px"
                            />
                        </ZTooltip>
                    }


                    {/* Import */}
                    {dataTable.importBulkUpdate !== false &&
                        <ZTooltip text="Import stock details">
                            <ZButton
                                size="sm"
                                className="btn-icon custom-btn-icon"
                                onClick={dataTable.importBulkUpdate} 
                                icon={ZIconType.Upload}
                                iconSize="18px"
                            />
                        </ZTooltip>
                    }

                    {/* Export */}
                    {disableExport !== true && <>
                            <Dropdown>
                                <ZTooltip text="Export">
                                    <Dropdown.Toggle variant="light" id="dropdown-basic" className="btn-icon custom-btn-icon">
                                        <ZIcon icon={ZIconType.Download} size="18px" />
                                    </Dropdown.Toggle>
                                </ZTooltip>
                                <Dropdown.Menu className="w-auto">
                                    <Dropdown.Item className="d-inline-flex align-items-center gap-1" onClick={() => { dataTable.exportData("csv", true) }}><ZIcon icon={ZIconType.File} /> CSV File</Dropdown.Item>
                                    <Dropdown.Item className="d-inline-flex align-items-center gap-1" onClick={() => { dataTable.exportData("xlsx", true) }}><ZIcon icon={ZIconType.Excel} /> Excel File</Dropdown.Item>
                                    <Dropdown.Item className="d-inline-flex align-items-center gap-1" onClick={() => { dataTable.exportData("pdf", true) }}><ZIcon icon={ZIconType.PDF} /> PDF File</Dropdown.Item>
                                    <ReactToPrint content={reactToPrintContent} trigger={reactToPrintTrigger} />
                                </Dropdown.Menu>
                            </Dropdown>
                    </>}
                </div>
            </Col>
        </Row>
    );
}
